import { msg } from "@lingui/core/macro";
import { createSignal, type JSX, Show } from "solid-js";
import { Dialog, DialogDescription, DialogTitle } from "./dialog";
import { Button } from "./button";
import { useI18n } from "@/providers/i18n-provider";
import { Loader2 } from "@fleet/icons";

interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  onConfirm: () => void;
  onCancel?: () => void;
  renderTrigger?: (open: () => void) => JSX.Element;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
}

/**
 * Confirmation dialog built on Kobalte's Dialog primitive.
 * Uses CSS transitions for smooth entrance and exit animations.
 */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const { t } = useI18n();
  const [internalOpen, setInternalOpen] = createSignal(false);

  const isOpen = () => props.open ?? internalOpen();
  const setIsOpen = (val: boolean) => {
    if (props.onOpenChange) {
      props.onOpenChange(val);
    } else {
      setInternalOpen(val);
    }
  };

  const handleConfirm = () => {
    props.onConfirm();
    // Only close automatically if we are not in controlled mode.
    // If props.open is provided, the parent should handle closing the dialog.
    if (props.open === undefined) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {props.renderTrigger?.(() => {
        setIsOpen(true);
      })}
      <Dialog
        open={isOpen()}
        onOpenChange={setIsOpen}
        role="alertdialog"
        contentClass="max-w-[400px] p-6"
      >
        <DialogTitle class="text-xl font-bold mb-2">{props.title}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-6">
          {props.description}
        </DialogDescription>
        <div class="flex justify-end gap-3 mt-6">
          <Button
            variant="ghost"
            class="h-11 px-5 rounded-xl font-bold"
            onClick={() => {
              props.onCancel?.();
              setIsOpen(false);
            }}
          >
            {props.cancelLabel ?? t(msg`Cancel`)}
          </Button>
          <Button
            variant={props.variant === "destructive" ? "destructive" : "default"}
            class="h-11 px-6 rounded-xl font-bold min-w-[120px]"
            onClick={handleConfirm}
            disabled={props.isLoading}
          >
            <Show when={props.isLoading} fallback={props.confirmLabel ?? t(msg`Confirm`)}>
              <Loader2 class="h-4 w-4 animate-spin mr-2" />
              {t(msg`Working...`)}
            </Show>
          </Button>
        </div>
      </Dialog>
    </>
  );
}
